import { useParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import ReactHtmlParser from "react-html-parser";
import { FormattedMessage } from "react-intl";

import { useGetRateCardDetails } from "api/queries/rate-card/useGetRateCardDetails";
import { replaceLinebreaks, formatNumber } from "helpers/commonHelpers";
import { replaceLinks } from "helpers/urlHelpers";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";

function RateCardGeneral() {
  const { id } = useParams();
  const { data } = useGetRateCardDetails(id as string, { refetchOnMount: false });

  return (
    <div>
      <Row>
        <Col lg="4" className="detail-info-container">
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="general.provider" defaultMessage="Provider" />
            </Col>
            <Col>{data?.provider || "-"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold text-capitalize">
              <FormattedMessage id="general.freightMode" defaultMessage="Freight Mode" />
            </Col>
            <Col>{data?.freight_mode || "-"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.startDate" defaultMessage="Start Date" />
            </Col>
            <Col>{formatDate(data?.rate_card_start_date, DateTimeFormat.dateFormat)}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.expiryDate" defaultMessage="Expiry Date" />
            </Col>
            <Col>{formatDate(data?.rate_card_expiration, DateTimeFormat.dateFormat)}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.incoterms" defaultMessage="Incoterms" />
            </Col>
            <Col>{data?.incoterms?.length ? data.incoterms.map((incoterm: string) => <div>{incoterm}</div>) : "-"}</Col>
          </Row>
        </Col>
        <Col lg="4" className="detail-info-container">
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.laneOrigin" defaultMessage="Lane - Origin" />
            </Col>
            <Col>{data?.lane_origin || "-"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.laneDestination" defaultMessage="Lane - Destination" />
            </Col>
            <Col>{data?.lane_destination || "-"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="general.status" defaultMessage="Status" />
            </Col>
            <Col>{data.is_active ? "Active" : "Inactive"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.dangerousGoods" defaultMessage="Status" />
            </Col>
            <Col>{data.is_dangerous_goods ? "Yes" : "No"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="new-request.service.serviceRequirement" defaultMessage="Service requirement" />
            </Col>
            <Col>
              {data?.service_requirement?.length
                ? data.service_requirement.map((requirement: string) => <div>{requirement}</div>)
                : "-"}
            </Col>
          </Row>
        </Col>
        <Col lg="4" className="detail-info-container">
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.pickupLeadTimeDay" defaultMessage="Pick Up Lead Time (day)" />
            </Col>
            <Col>{formatNumber(data?.pickup_lead_time_in_days) || "-"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.deliveryLeadTimeDay" defaultMessage="Delivery Lead Time (day)" />
            </Col>
            <Col>{formatNumber(data?.delivery_lead_time_in_days) || "-"}</Col>
          </Row>
          {data?.freight_mode !== "Ocean freight | FCL" && (
            <Row className="mb-2">
              <Col className="font-weight-bold">
                <FormattedMessage id="settings.rate-cards.metricShippingFactor" defaultMessage="Metric Shipping Factor" />
              </Col>
              <Col>{formatNumber(data?.metric_shipping_factor) || "-"}</Col>
            </Row>
          )}
          {!["Regular air freight", "Road freight", "Next flight out (NFO)", "Rail freight", "Ocean freight | FCL"].includes(
            data?.freight_mode,
          ) && (
            <>
              <Row className="mb-2">
                <Col className="font-weight-bold">
                  <FormattedMessage id="settings.rate-cards.charge_type" defaultMessage="Charge Type" />
                </Col>
                <Col>{data?.lcl_charge_type || "-"}</Col>
              </Row>
              <Row className="mb-2">
                <Col className="font-weight-bold">
                  <FormattedMessage id="settings.rate-cards.tonnePerCBM" defaultMessage="Tonne per CBM" />
                </Col>
                <Col>{data?.tonne_per_cbm || "-"}</Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg="7" className="additional-info-container">
          <Row className="mb-2">
            <Col className="font-weight-bold text-capitalize">
              <FormattedMessage id="general.additionalComments" defaultMessage="Additional Comments" />
            </Col>
            <Col>{ReactHtmlParser(replaceLinebreaks(replaceLinks(data?.additional_comments || "-")))}</Col>
          </Row>
        </Col>
        <Col lg="5" className="additional-info-container">
          <Row className="mb-2">
            <Col className="font-weight-bold">
              <FormattedMessage id="settings.rate-cards.notIncluded" defaultMessage="Not Included" />
            </Col>
            <Col>{ReactHtmlParser(replaceLinebreaks(data?.not_included || "-"))}</Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
}

export default RateCardGeneral;
